/**
 * Error thrown for a failed `POST /api/transcriptions/transcribe`.
 *
 * Billing blocks keep their structured detail so the upload UI can say why:
 * the card-backed trial's `trial_analysis_used` and the 402 no-credit block
 * (`creditErrors.ts`). Neither is a rate limit, so neither becomes
 * `RATE_LIMITED`. The voice-minute quota is a 429 with a coded detail
 * (`voice_minutes_exceeded`) and keeps it too, so the UI can show the
 * minutes left; any other 429 is a rate limit. A refused recording (400, or
 * the 413 body cap) carries an `audio_*` code (JOB-R22) and keeps it, so the
 * UI can say why and what to do next.
 */
import { AUDIO_UPLOAD_LABELS } from './audioUploadFormats';
import { isNoCreditError } from './creditErrors';
import { analyticsErrorCode } from '../services/analyticsPrivacy';
import { apiErrorFromBody, getTrialAnalysisUsed } from './trialAnalysisUsed';

export const VOICE_MINUTES_EXCEEDED_CODE = 'voice_minutes_exceeded';

/** Backend `app.utils.audio_formats` rejection code -> `transcription.errors.*` key. */
const AUDIO_REJECTION_KEYS: Record<string, string> = {
  audio_too_large: 'transcription.errors.audioTooLarge',
  audio_too_long: 'transcription.errors.audioTooLong',
  audio_unsupported_format: 'transcription.errors.audioUnsupportedFormat',
  audio_unreadable: 'transcription.errors.audioUnreadable',
};

export function transcribeErrorFromResponse(status: number, body: unknown): Error {
  const structured = apiErrorFromBody(body, status, 'TRANSCRIPTION_FAILED');
  if (getTrialAnalysisUsed(body) || isNoCreditError(structured)) return structured;
  if (structured.code === VOICE_MINUTES_EXCEEDED_CODE) return structured;
  if (structured.code && AUDIO_REJECTION_KEYS[structured.code]) return structured;
  if (status === 413) return new Error('UPLOAD_FILE_TOO_LARGE');
  if (status === 415) return new Error('UNSUPPORTED_AUDIO_FORMAT');
  if (status === 401) return new Error('AUTH_EXPIRED');
  if (status === 429) return new Error('RATE_LIMITED');
  if (status >= 500) return new Error('SERVER_UNAVAILABLE');
  return structured;
}

type Translate = (key: string, ...rest: any[]) => string;

function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Localized copy for the voice-minute quota block (429
 * `voice_minutes_exceeded`), or null for any other error. Uses the plan's
 * minute limit and the minutes left when the detail carries them.
 */
export function voiceMinutesExceededMessage(t: Translate, error: unknown): string | null {
  const detail = (error as { detail?: unknown } | null | undefined)?.detail;
  if (!detail || typeof detail !== 'object') return null;
  const record = detail as Record<string, unknown>;
  if (record.code !== VOICE_MINUTES_EXCEEDED_CODE) return null;
  const limit = finiteNumber(record.limit_minutes);
  const remaining = finiteNumber(record.remaining_minutes);
  if (limit === null || remaining === null) return t('errors.voiceMinutesExceededGeneric');
  return t(record.is_trial === true ? 'errors.voiceMinutesExceededTrial' : 'errors.voiceMinutesExceeded', {
    limit,
    remaining,
  });
}

/**
 * Localized copy for a refused recording (`audio_too_large`, `audio_too_long`,
 * `audio_unsupported_format`, `audio_unreadable`), or null for any other
 * error. The size and duration limits come from the server's detail.
 */
export function audioUploadRejectionMessage(t: Translate, error: unknown): string | null {
  const detail = (error as { detail?: unknown } | null | undefined)?.detail;
  if (!detail || typeof detail !== 'object') return null;
  const record = detail as Record<string, unknown>;
  const key = typeof record.code === 'string' ? AUDIO_REJECTION_KEYS[record.code] : undefined;
  if (!key) return null;
  if (record.code === 'audio_too_large') {
    return t(key, { limit: finiteNumber(record.limit_mb) ?? 100 });
  }
  if (record.code === 'audio_too_long') {
    return t(key, { limit: finiteNumber(record.limit_minutes) ?? 60 });
  }
  if (record.code === 'audio_unsupported_format') {
    const labels = Array.isArray(record.supported_formats) && record.supported_formats.length
      ? record.supported_formats.map(String)
      : AUDIO_UPLOAD_LABELS;
    return t(key, { formats: labels.join(', ') });
  }
  return t(key);
}

/** The backend's audio rejection code (`audio_too_large`, ...) or null for any other failure. */
export function audioUploadRejectionCode(error: unknown): string | null {
  const detail = (error as { detail?: unknown } | null | undefined)?.detail;
  if (!detail || typeof detail !== 'object') return null;
  const code = (detail as Record<string, unknown>).code;
  return typeof code === 'string' && AUDIO_REJECTION_KEYS[code] ? code : null;
}

export type AudioUploadSurface = 'chat' | 'library' | 'mentor_engagement';

/**
 * The analytics event for a failed recording upload/transcription: a coded
 * refusal is `audio_upload_rejected` (the funnel's "why did recordings never
 * reach transcription"), anything else `audio_upload_failed` (the recording
 * flow failed in the UI; the server's `transcription_failed` is the truth for
 * the job itself). Never the error text, never the file name.
 */
export function transcriptionFailureEvent(
  error: unknown,
  surface: AudioUploadSurface,
): { event: 'audio_upload_rejected' | 'audio_upload_failed'; properties: Record<string, string> } {
  const rejection = audioUploadRejectionCode(error);
  if (rejection) {
    return { event: 'audio_upload_rejected', properties: { error_code: rejection, surface, job_kind: 'transcription' } };
  }
  return {
    event: 'audio_upload_failed',
    properties: { error_code: analyticsErrorCode(error), surface, job_kind: 'transcription' },
  };
}
